import React, { useState, useEffect, useRef } from "react";
import { listenerMiddleware } from "../store";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../slices/authSlice";
import { useLazyGetOrganizationAccessQuery } from "../slices/employeesApiSlice";
import { useLazyGetPortalUserInfoQuery } from "../slices/dataviewApiSlice";
import { useLazyGetSessionInfoQuery } from "../slices/sessionSlice";
import Button from "./ui/Button";
import { useTimer } from "react-timer-hook";
import { dateToLocalTimestamp } from "../utils/helpers";
import moment from "moment";
import PropTypes from "prop-types";
import GTM from "../GTM";
import { clearAllCache, resetCacheExpiry } from "../utils/indexedDBCache";

// How often (ms) we allow a keep-alive ping while the user is active.

function SessionTimeout({ showModal = false }) {
    const gtm = new GTM(null);
    const hasFiredTimeoutImpression = useRef(false);
    const lastKeepAlivePingRef = useRef(0);
    const handleStartRef = useRef();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { organizationsAccess } = useSelector((state) => state.organizations);
    const flags = useSelector((state) => state.app?.flags);
    const [getOrganizationAccess, { data = null, error, isFetching }] =
        useLazyGetOrganizationAccessQuery();
    const [getPortalUser, { error: portalUserError }] =
        useLazyGetPortalUserInfoQuery();
    const [getSessionInfo] = useLazyGetSessionInfoQuery();
    const [displayPopup, setDisplayPopup] = useState(false);
    const [show, setShow] = useState(showModal);
    const [countdownComplete, setCountdownComplete] = useState(false);
    const { totalSeconds, pause, restart } = useTimer({
        expiryTimestamp: new Date(),
        autoStart: false,
        onExpire: () => {
            setCountdownComplete(true);
        },
    });

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    // 5 minutes
    const KEEP_ALIVE_THRESHOLD = 5 * 60 * 1000;

    const onIdle = async () => {
        // Clear cache before logging out due to session timeout
        try {
            await clearAllCache();
        } catch (error) {
            console.error("Error clearing cache on session timeout:", error);
        }

        dispatch(logout());
        handleClose();
    };

    const handleStart = async (user) => {
        if (user) {
            if (user?.isUserInternal) {
                const minutes =
                    process.env.REACT_APP_SESSION_LIFETIME_IN_SECONDS / 60;
                const expirationTime = new Date(
                    new Date().getTime() + minutes * 60000
                );
                restart(expirationTime);
            } else {
                sessionInfoCall();
            }
        }
    };

    const sessionInfoCall = async () => {
        try {
            const sessionData = await getSessionInfo().unwrap();

            const sessionTimeout = moment(
                dateToLocalTimestamp(sessionData.maxIdleExpirationTime)
            ).local()._d;

            restart(sessionTimeout);
        } catch (err) {
            console.error("sessionInfoCall failed:", err);
        }
    };

    // Always keep the ref pointing at the latest handleStart so that
    // long-lived listeners (activity events, middleware) never call a
    // stale closure.
    handleStartRef.current = handleStart;

    useEffect(() => {
        const threshold = Number(
            process.env.REACT_APP_SESSION_COUNTDOWN_TIMER_IN_SECONDS
        );

        //Once timer is healthy again, allow popup in future
        if (totalSeconds > threshold) {
            setDisplayPopup(false);
            return;
        }

        // Show popup only when allowed
        if (totalSeconds > 0 && totalSeconds <= threshold && !displayPopup) {
            if (!show) handleShow();
            setDisplayPopup(true);
        }
    }, [totalSeconds, displayPopup, show]);

    useEffect(() => {
        if ((error || portalUserError)?.status === 401) {
            dispatch(logout());
        }
    }, [data, isFetching, error, portalUserError]);

    useEffect(() => {
        const unsubscribe = listenerMiddleware.startListening({
            predicate: (action, currentState) => {
                if (String(action.type).includes("executeQuery/fulfilled")) {
                    if (!action.type.includes("sessionSlice/executeQuery")) {
                        handleStartRef.current(currentState?.auth?.user);
                    }
                }
            },
            effect: async () => {},
        });

        return unsubscribe;
    }, []);

    const resetFrontendTimer = () => {
        const expirationTime = new Date(
            Date.now() +
                Number(process.env.REACT_APP_SESSION_LIFETIME_IN_SECONDS) * 1000
        );

        restart(expirationTime);
    };
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (user) {
            resetFrontendTimer();
        }
    }, [user]);

    // ODS flow keeps the session alive through the Portal Users API,
    // IDHP flow through the organization access API.
    const refreshUserAccess = async () => {
        const orgIds = Object.keys(organizationsAccess || {});

        if (flags?.isODSEnabled) {
            // Portal Users is keyed by user + org, so skip until both are known.
            if (!user?.userId || orgIds.length === 0) return;

            await getPortalUser({
                isfUserId: String(user.userId).toUpperCase(),
                organizationId: orgIds.join(","),
            }).unwrap();

            return;
        }

        await getOrganizationAccess({
            organizationIds: orgIds.join(","),
        }).unwrap();
    };

    const refreshExternalSession = async () => {
        pause();

        try {
            await refreshUserAccess();

            await sessionInfoCall();
            await resetCacheExpiry();
        } catch (error) {
            console.error("Error resetting cache expiry:", error);
        }
    };

    // Keep the server-side session alive while the user is active,
    // even when their activity does not trigger an API call.

    useEffect(() => {
        if (!user) return;

        const resetSession = () => {
            if (show || countdownComplete) return;

            clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(async () => {
                if (!user) return;

                // Internal users
                if (user?.isUserInternal) {
                    resetFrontendTimer();
                } else {
                    // External users
                    const now = Date.now();

                    // Call keep-alive APIs only once every 5 minutes
                    if (
                        now - lastKeepAlivePingRef.current >=
                        KEEP_ALIVE_THRESHOLD
                    ) {
                        lastKeepAlivePingRef.current = now;

                        await refreshExternalSession();
                    }
                }
            }, 300);
        };

        const events = [
            "mousemove",
            "mousedown",
            "click",
            "keydown",
            "scroll",
            "touchstart",
            "wheel",
            "input",
        ];

        events.forEach((event) => {
            document.addEventListener(event, resetSession, true);
        });

        return () => {
            events.forEach((event) => {
                document.removeEventListener(event, resetSession, true);
            });

            clearTimeout(timeoutRef.current);
        };
    }, [user, show, countdownComplete]);

    useEffect(() => {
        if (show && countdownComplete && !hasFiredTimeoutImpression.current) {
            hasFiredTimeoutImpression.current = true;
            gtm.Event(
                "timeout modal",
                "modal",
                "impression",
                "timeout modal",
                "timeout modal",
                "undefined",
                "timeout_modal_impression",
                "undefined"
            );
        }
    }, [show, countdownComplete]);

    useEffect(() => {
        if (!show || !countdownComplete) {
            hasFiredTimeoutImpression.current = false;
        }
    }, [show, countdownComplete]);

    const continueSession = async () => {
        pause();

        // Close modal immediately
        handleClose();

        // Prevent popup from reopening while timer is still low
        setDisplayPopup(true);

        // Reset throttle
        lastKeepAlivePingRef.current = Date.now();

        try {
            if (user?.isUserInternal) {
                // INTERNAL USER → Frontend timer only
                const expirationTime = new Date(
                    Date.now() +
                        Number(
                            process.env.REACT_APP_SESSION_LIFETIME_IN_SECONDS
                        ) *
                            1000
                );

                restart(expirationTime);
            } else {
                // EXTERNAL USER → Backend-driven session
                await refreshUserAccess();

                const sessionData = await getSessionInfo().unwrap();

                const sessionTimeout = moment(
                    dateToLocalTimestamp(sessionData.maxIdleExpirationTime)
                ).local()._d;

                // Restart timer with backend expiry
                restart(sessionTimeout);
            }

            await resetCacheExpiry();
        } catch (error) {
            console.error("Error refreshing session:", error);
        }
    };

    return (
        <div>
            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered={true}
                size={"sm"}
            >
                <Modal.Header
                    style={{
                        background: "#E6EFF5",
                        padding: 16,
                    }}
                >
                    <span className="fs-sm text-bold">Attention</span>
                    <button
                        className="fa-solid fa-xmark cursor-pointer border-0 bg-transparent text-secondary"
                        onClick={countdownComplete ? onIdle : handleClose}
                        aria-label="Close"
                        type="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (countdownComplete) {
                                    onIdle();
                                } else {
                                    handleClose();
                                }
                            }
                        }}
                    ></button>
                </Modal.Header>

                <Modal.Body
                    style={{
                        padding: 32,
                    }}
                >
                    {countdownComplete && (
                        <>
                            <div className="text-semibold">
                                <p>
                                    You have been signed out due to inactivity.
                                </p>
                            </div>
                            <div className="col-12">
                                <Button
                                    text="Login"
                                    onClick={onIdle}
                                    data-testid="logout"
                                    tabIndex={0}
                                />
                            </div>
                        </>
                    )}

                    {!countdownComplete && (
                        <>
                            <div className="mb-16p">
                                <span className="mb-16p text-semibold">
                                    Your session is about to expire. <br /> You
                                    will be logged out in{" "}
                                    {Math.floor(totalSeconds / 60)} min{" "}
                                    {totalSeconds % 60} sec.
                                </span>
                            </div>

                            <div className="row align-items-center">
                                <div className="col-6">
                                    <Button
                                        text="Log out"
                                        onClick={onIdle}
                                        data-testid="logout"
                                        tabIndex={0}
                                    />
                                </div>

                                <div className="col-6">
                                    <button
                                        className="text-primary-main cursor-pointer border-0 bg-transparent"
                                        onClick={continueSession}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                e.preventDefault();
                                                continueSession();
                                            }
                                        }}
                                        data-testid="continue"
                                        style={{
                                            fontWeight: "bold",
                                            marginLeft: 30,
                                        }}
                                        tabIndex={0}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

SessionTimeout.propTypes = {
    showModal: PropTypes.bool,
};

export default SessionTimeout;
